<template lang="html">
  <sc-flex :is-column="true" :is-full-width="true">
    <!-- Partial: Alert -->
    <TaxReturnAlert>
      <sc-alert type="info">
        Please review the tax return using the index on the left to navigate
        through pages or the arrow buttons below. You can also download the
        draft for review. Once you have finished reviewing it, please click the
        <b>Continue</b> button.
        <sc-button-group slot="button-group">
          <sc-button
            type="ghost"
            @click="downloadTaxReturn()"
            :is-loading="downloadingTaxReturn"
            data-test-id="download-draft-button"
          >
            Download Draft
          </sc-button>
          <sc-button
            type="ghost"
            @click="$emit('finished-reviewing')"
            icon-right="arrow-right"
            :is-disabled="saving || pdfLoading || !hasNextStep"
            data-test-id="continue-review-button"
          >
            Continue
          </sc-button>
        </sc-button-group>
      </sc-alert>
    </TaxReturnAlert>

    <!-- Partial: Content -->
    <sc-flex>
      <aside class="sidebar hidden-sm-and-down" v-if="bookmarks.length > 0">
        <v-card class="sidebar-card">
          <v-layout class="mx-3 mt-3 mb-2">
            <sc-h3 class="index-title">Index</sc-h3>
          </v-layout>

          <v-flex class="caption ma-3 text-xs-left">
            The index is generated from the PDF's bookmarks and the auto
            classification tool. You can click on a page to navigate to it.
          </v-flex>

          <v-list dense expand>
            <v-list-tile
              v-if="hasTaxSummary"
              @click="currentPage = 0"
              :style="
                currentPage === 0 ? 'background-color: var(--blue-100)' : ''
              "
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  <v-layout justify-space-between align-center>
                    <span>Tax Summary</span>
                  </v-layout>
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <template v-for="(bookmark, index) in bookmarks">
              <v-list-group
                v-if="bookmark.items && bookmark.items.length > 0"
                :key="bookmark.title + ' ' + index"
                :id="`bookmark-${bookmark.pageIndex}`"
              >
                <template v-slot:activator>
                  <v-list-tile
                    :style="
                      pageWithinBookmark(bookmark)
                        ? 'background-color: var(--blue-100)'
                        : ''
                    "
                    @click="goToPage(bookmark.pageIndex)"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title style="text-decoration: underline">
                        {{ bookmark.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </template>

                <v-list
                  dense
                  expand
                  class="pl-0 ml-3 text-align-left pt-0 pb-0"
                >
                  <template v-for="(subItem, subIndex) in bookmark.items">
                    <v-list-group
                      v-if="subItem.items && subItem.items.length > 0"
                      :key="subItem.title + ' ' + subIndex"
                    >
                      <template v-slot:activator>
                        <v-list-tile @click="goToPage(subItem.pageIndex)">
                          <v-list-tile-content>
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <span>{{ subItem.title }}</span>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </template>
                      <v-list
                        dense
                        expand
                        class="pl-0 ml-3 text-align-left pt-0 pb-0"
                      >
                        <template
                          v-for="(thirdItem, thirdIndex) in subItem.items"
                        >
                          <v-list-group
                            v-if="thirdItem.items && thirdItem.items.length > 0"
                            :key="thirdItem.title + ' ' + thirdIndex"
                          >
                            <template v-slot:activator>
                              <v-list-tile
                                @click="goToPage(thirdItem.pageIndex)"
                              >
                                <v-list-tile-content>
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <span>{{ thirdItem.title }}</span>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile-content>
                              </v-list-tile>
                            </template>
                            <v-list
                              dense
                              expand
                              class="pl-0 ml-3 text-align-left pt-0 pb-0"
                            >
                              <template
                                v-for="(fourthItem,
                                fourthIndex) in thirdItem.items"
                              >
                                <v-list-group
                                  v-if="
                                    fourthItem.items &&
                                      fourthItem.items.length > 0
                                  "
                                  :key="fourthItem.title + ' ' + fourthIndex"
                                >
                                  <template v-slot:activator>
                                    <v-list-tile
                                      @click="goToPage(fourthItem.pageIndex)"
                                    >
                                      <v-list-tile-content>
                                        <v-list-tile-title>
                                          <v-layout
                                            justify-space-between
                                            align-center
                                          >
                                            <span>{{ fourthItem.title }}</span>
                                          </v-layout>
                                        </v-list-tile-title>
                                      </v-list-tile-content>
                                    </v-list-tile>
                                  </template>
                                  <v-list-tile
                                    v-for="fifthItem in fourthItem.items"
                                    :key="
                                      fifthItem.title +
                                        ' ' +
                                        fifthItem.pageIndex
                                    "
                                    @click="goToPage(fifthItem.pageIndex)"
                                    :style="
                                      selectedThumbnail === fifthItem.pageIndex
                                        ? 'background-color: var(--blue-100)'
                                        : {}
                                    "
                                    class="pl-4"
                                  >
                                    <v-list-tile-title>
                                      <v-layout
                                        justify-space-between
                                        align-center
                                      >
                                        <v-layout justify-start align-center>
                                          <template
                                            v-if="
                                              isOverflowing(
                                                'bookmarkTitle' +
                                                  fifthItem.pageIndex,
                                              )
                                            "
                                          >
                                            <v-tooltip bottom>
                                              <template
                                                v-slot:activator="{ on, attrs }"
                                              >
                                                <span
                                                  class="bookmark"
                                                  v-on="on"
                                                  v-bind="attrs"
                                                  :ref="
                                                    'bookmarkTitle' +
                                                      fifthItem.pageIndex
                                                  "
                                                  >{{ fifthItem.title }}</span
                                                >
                                              </template>
                                              <span>{{ fifthItem.title }}</span>
                                            </v-tooltip>
                                          </template>
                                          <template v-else>
                                            <span
                                              class="bookmark"
                                              :ref="
                                                'bookmarkTitle' +
                                                  fifthItem.pageIndex
                                              "
                                              >{{ fifthItem.title }}</span
                                            >
                                          </template>
                                          <v-chip
                                            v-if="
                                              isFilingInstructionsPage(
                                                fifthItem.pageIndex,
                                              )
                                            "
                                            small
                                            class="my-0 py-0 filing-instructions-chip"
                                          >
                                            Filing Instructions
                                          </v-chip>
                                        </v-layout>
                                      </v-layout>
                                    </v-list-tile-title>
                                  </v-list-tile>
                                </v-list-group>
                                <v-list-tile
                                  v-else-if="fourthItem.pageIndex"
                                  :key="
                                    fourthItem.title +
                                      ' ' +
                                      fourthItem.pageIndex
                                  "
                                  @click="goToPage(fourthItem.pageIndex)"
                                  :style="
                                    selectedThumbnail === fourthItem.pageIndex
                                      ? 'background-color: var(--blue-100)'
                                      : {}
                                  "
                                  class="pl-4"
                                >
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <v-layout justify-start align-center>
                                        <template
                                          v-if="
                                            isOverflowing(
                                              'bookmarkTitle' +
                                                fourthItem.pageIndex,
                                            )
                                          "
                                        >
                                          <v-tooltip bottom>
                                            <template
                                              v-slot:activator="{ on, attrs }"
                                            >
                                              <span
                                                class="bookmark"
                                                v-on="on"
                                                v-bind="attrs"
                                                :ref="
                                                  'bookmarkTitle' +
                                                    fourthItem.pageIndex
                                                "
                                                >{{ fourthItem.title }}</span
                                              >
                                            </template>
                                            <span>{{ fourthItem.title }}</span>
                                          </v-tooltip>
                                        </template>
                                        <template v-else>
                                          <span
                                            class="bookmark"
                                            :ref="
                                              'bookmarkTitle' +
                                                fourthItem.pageIndex
                                            "
                                            >{{ fourthItem.title }}</span
                                          >
                                        </template>
                                        <v-chip
                                          v-if="
                                            isFilingInstructionsPage(
                                              fourthItem.pageIndex,
                                            )
                                          "
                                          small
                                          class="my-0 py-0 filing-instructions-chip"
                                        >
                                          Filing Instructions
                                        </v-chip>
                                      </v-layout>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile>
                              </template>
                            </v-list>
                          </v-list-group>
                          <v-list-tile
                            v-else-if="thirdItem.pageIndex"
                            :key="thirdItem.title + ' ' + thirdItem.pageIndex"
                            @click="goToPage(thirdItem.pageIndex)"
                            :style="
                              selectedThumbnail === thirdItem.pageIndex
                                ? 'background-color: var(--blue-100)'
                                : {}
                            "
                            class="pl-4"
                          >
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <v-layout justify-start align-center>
                                  <template
                                    v-if="
                                      isOverflowing(
                                        'bookmarkTitle' + thirdItem.pageIndex,
                                      )
                                    "
                                  >
                                    <v-tooltip bottom>
                                      <template
                                        v-slot:activator="{ on, attrs }"
                                      >
                                        <span
                                          class="bookmark"
                                          v-on="on"
                                          v-bind="attrs"
                                          :ref="
                                            'bookmarkTitle' +
                                              thirdItem.pageIndex
                                          "
                                          >{{ thirdItem.title }}</span
                                        >
                                      </template>
                                      <span>{{ thirdItem.title }}</span>
                                    </v-tooltip>
                                  </template>
                                  <template v-else>
                                    <span
                                      class="bookmark"
                                      :ref="
                                        'bookmarkTitle' + thirdItem.pageIndex
                                      "
                                      >{{ thirdItem.title }}</span
                                    >
                                  </template>
                                  <v-chip
                                    v-if="
                                      isFilingInstructionsPage(
                                        thirdItem.pageIndex,
                                      )
                                    "
                                    small
                                    class="my-0 py-0 filing-instructions-chip"
                                  >
                                    Filing Instructions
                                  </v-chip>
                                </v-layout>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile>
                        </template>
                      </v-list>
                    </v-list-group>
                    <v-list-tile
                      v-else-if="subItem.pageIndex"
                      :key="subItem.title + ' ' + subItem.pageIndex"
                      @click="goToPage(subItem.pageIndex)"
                      :style="
                        selectedThumbnail === subItem.pageIndex
                          ? 'background-color: var(--blue-100)'
                          : {}
                      "
                      class="pl-4"
                    >
                      <v-list-tile-title>
                        <v-layout justify-space-between align-center>
                          <v-layout justify-start align-center>
                            <template
                              v-if="
                                isOverflowing(
                                  'bookmarkTitle' + subItem.pageIndex,
                                )
                              "
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <span
                                    class="bookmark"
                                    v-on="on"
                                    v-bind="attrs"
                                    :ref="'bookmarkTitle' + subItem.pageIndex"
                                    >{{ subItem.title }}</span
                                  >
                                </template>
                                <span>{{ subItem.title }}</span>
                              </v-tooltip>
                            </template>
                            <template v-else>
                              <span
                                class="bookmark"
                                :ref="'bookmarkTitle' + subItem.pageIndex"
                                >{{ subItem.title }}</span
                              >
                            </template>
                            <v-chip
                              v-if="isFilingInstructionsPage(subItem.pageIndex)"
                              small
                              class="my-0 py-0 filing-instructions-chip"
                            >
                              Filing Instructions
                            </v-chip>
                          </v-layout>
                        </v-layout>
                      </v-list-tile-title>
                    </v-list-tile>
                  </template>
                </v-list>
              </v-list-group>

              <v-list-tile
                v-else-if="bookmark.pageIndex"
                :key="bookmark.title + ' ' + index + 'no items'"
                :id="`bookmark-${bookmark.pageIndex}`"
                @click="goToPage(bookmark.pageIndex)"
                :style="
                  selectedThumbnail === bookmark.pageIndex
                    ? 'background-color: var(--blue-100)'
                    : ''
                "
              >
                <v-list-tile-title>
                  <v-layout align-center>
                    <template
                      v-if="isOverflowing('bookmarkTitle' + bookmark.pageIndex)"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            class="bookmark"
                            v-on="on"
                            v-bind="attrs"
                            :ref="'bookmarkTitle' + bookmark.pageIndex"
                            :style="
                              bookmark.title.startsWith('Page')
                                ? 'width: 50px;'
                                : ''
                            "
                            >{{ bookmark.title }}</span
                          >
                        </template>
                        <span>{{ bookmark.title }}</span>
                      </v-tooltip>
                    </template>
                    <template v-else>
                      <span
                        class="bookmark"
                        :ref="'bookmarkTitle' + bookmark.pageIndex"
                        :style="
                          bookmark.title.startsWith('Page')
                            ? 'width: 50px;'
                            : ''
                        "
                        >{{ bookmark.title }}</span
                      >
                    </template>
                    <v-chip
                      v-if="isFilingInstructionsPage(bookmark.pageIndex)"
                      small
                      class="my-0 py-0 filing-instructions-chip"
                    >
                      Filing Instructions
                    </v-chip>
                  </v-layout>
                </v-list-tile-title>
              </v-list-tile>
            </template>
          </v-list>
        </v-card>
      </aside>
      <!-- State: Loading -->
      <v-layout wrap>
        <v-layout
          v-if="pdfLoading"
          align-center
          justify-center
          style="height: 100vh; width: 100%"
        >
          <v-progress-circular indeterminate color="primary" />
        </v-layout>

        <!-- State: Partial Pages -->
        <section class="pdf-page-container" v-else>
          <div class="pdf-review-step__navigation">
            <sc-button
              type="secondary"
              @click="prevPage"
              :is-disabled="
                hasTaxSummary ? currentPage === 0 : currentPage === 1
              "
              icon-left="arrow-left"
              size="small"
              >Prev page</sc-button
            >

            <v-chip class="my-2 mb-1 primary white--text" small>
              <span v-if="currentPage === 0">
                Tax Summary
              </span>

              <span v-else> Page {{ currentPage }} of {{ pageCount }} </span>
            </v-chip>

            <sc-button
              type="secondary"
              @click="nextPage"
              :is-disabled="currentPage === pageCount"
              icon-right="arrow-right"
              size="small"
              >Next page</sc-button
            >
          </div>

          <v-layout column justify-center>
            <v-layout
              column
              align-center
              v-if="currentPage > 0 && currentPageImage"
            >
              <img :src="currentPageImage.thumbnail" class="thumbnail" />
            </v-layout>

            <v-layout
              column
              v-else-if="taxReturn !== null && currentPage === 0"
            >
              <TaxSummary :taxReturn="taxReturn" />
            </v-layout>

            <div class="pdf-review-step__bottom-navigation">
              <sc-button
                type="secondary"
                @click="prevPage"
                :is-disabled="
                  hasTaxSummary ? currentPage === 0 : currentPage === 1
                "
                icon-left="arrow-left"
                size="small"
                >Prev page</sc-button
              >

              <v-chip class="my-2 mb-1 primary white--text" small>
                <span v-if="currentPage === 0">
                  Tax Summary
                </span>

                <span v-else> Page {{ currentPage }} of {{ pageCount }} </span>
              </v-chip>

              <sc-button
                type="secondary"
                @click="nextPage"
                :is-disabled="currentPage === pageCount"
                icon-right="arrow-right"
                size="small"
                >Next page</sc-button
              >
            </div>
          </v-layout>
        </section>
      </v-layout>
    </sc-flex>
  </sc-flex>
</template>

<script>
import API from '@/api';
import { PDFDocument } from 'pdf-lib';
import pdf from 'vue-pdf';
import TaxReturnAlert from './components/TaxReturnAlert';
import TaxSummary from './components/TaxSummary';
import { generatePdfThumbnails } from '@/utils/pdf';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import { mapGetters } from 'vuex';

export default {
  components: {
    pdf,
    TaxReturnAlert,
    TaxSummary,
  },

  props: {
    companyId: {
      type: [String, Number],
      default: undefined,
    },

    taxReturn: {
      type: Object,
      default: null,
    },

    hasNextStep: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      bookmarks: [],
      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfLoaded: [],
      pageImages: [],
      isScrolled: false,
      saving: undefined,
      downloadingTaxReturn: false,
      selectedThumbnail: null,
      currentPage: 1,
    };
  },
  mixins: [UtilsMixins, FirmPreferenceMixins],
  methods: {
    isOverflowing(refName) {
      const el = this.$refs[refName];
      if (!el) return false;
      const node = Array.isArray(el) ? el[0] : el;
      if (!node) return false;
      return node.scrollWidth > node.clientWidth;
    },
    pageLoaded(page) {
      this.pdfLoaded[page] = true;
    },

    async downloadTaxReturn() {
      try {
        this.downloadingTaxReturn = true;
        const response = await API.taxReturn.downloadTaxReturn({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
        });

        this.downloadDocumentByLink(response.data.link);
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading tax return.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.downloadingTaxReturn = false;
      }
    },

    async extractBookmarks(pdfSrc) {
      try {
        const pdflib = window.pdfjsLib;
        pdflib.GlobalWorkerOptions.workerSrc =
          'https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.mjs';

        const loadingTask = pdflib.getDocument({ url: pdfSrc });
        const pdf = await loadingTask.promise; // Store pdf.js document instance

        this.extractBookmarksFromPdf(pdf);
      } catch (error) {
        console.error('Error extracting bookmarks:', error);
      }
    },

    async extractBookmarksFromPdf(pdf) {
      try {
        const outline = await pdf.getOutline();

        if (!outline) {
          this.bookmarks = [];
        } else {
          const processBookmarks = async bookmarks => {
            const processed = await Promise.all(
              bookmarks.map(async bookmark => {
                let pageIndex = null;

                // Extract page index correctly from dest[0]
                if (
                  bookmark.dest &&
                  bookmark.dest.length > 0 &&
                  bookmark.dest[0]
                ) {
                  try {
                    const resolvedPageIndex = await pdf.getPageIndex(
                      bookmark.dest[0],
                    );
                    pageIndex = resolvedPageIndex + 1; // Convert to 1-based index
                  } catch (error) {
                    console.warn(
                      `Failed to resolve page index for: ${bookmark.title}`,
                      error,
                    );
                  }
                }

                return {
                  title: bookmark.title,
                  pageIndex: pageIndex,
                  items: bookmark.items
                    ? await processBookmarks(bookmark.items)
                    : [],
                };
              }),
            );

            return processed;
          };

          let processedBookmarks = await processBookmarks(outline);

          const visiblePages = new Set(
            this.clientVisiblePages.map(p => p.page),
          );
          const filterByVisiblePages = items => {
            return (items || []).reduce((acc, b) => {
              const filteredChildren = filterByVisiblePages(b.items);
              const isVisibleLeaf = b.pageIndex
                ? visiblePages.has(b.pageIndex)
                : false;
              // Keep if it points to a visible page, or has any visible children
              if (isVisibleLeaf || filteredChildren.length > 0) {
                acc.push({ ...b, items: filteredChildren });
              }
              return acc;
            }, []);
          };
          processedBookmarks = filterByVisiblePages(processedBookmarks);

          // If there's only one top-level bookmark and it has items, use its items as root
          if (
            processedBookmarks.length === 1 &&
            processedBookmarks[0].items.length > 0
          ) {
            this.bookmarks = processedBookmarks[0].items;
          } else {
            this.bookmarks = processedBookmarks;
          }
        }

        this.insertSkippedPagesIntoBookmarks();
      } catch (error) {
        console.error('Error processing bookmarks:', error);
      }
    },

    insertSkippedPagesIntoBookmarks() {
      const allBookmarkedPages = this.bookmarksFlattened;
      const visiblePages = new Set(this.clientVisiblePages.map(p => p.page));
      for (let page = 1; page <= this.pageImages.length; page++) {
        if (!allBookmarkedPages.includes(page) && visiblePages.has(page)) {
          this.bookmarks.push({
            pageIndex: page,
            title: `Page ${page}`,
            items: [],
          });
        }
      }
      // Sort bookmarks by pageIndex
      this.bookmarks.sort((a, b) => a.pageIndex - b.pageIndex);
    },

    goToPage(pageIndex) {
      const visiblePageIndex = this.clientVisiblePages.findIndex(
        page => page.page === pageIndex,
      );

      if (visiblePageIndex !== -1) {
        this.currentPage = visiblePageIndex + 1;
        this.selectedThumbnail = pageIndex;
      }
    },

    pageExists(pageIndex) {
      return this.clientVisiblePages.some(page => page.page === pageIndex);
    },

    isFilingInstructionsPage(pageIndex) {
      if (this.showFilingInstructionsToClients) {
        return this.taxReturn.filing_instructions.some(
          instruction => instruction.page === pageIndex,
        );
      }
      return false;
    },

    pageWithinBookmark(bookmark) {
      return bookmark.items.some(
        item => item.pageIndex === this.selectedThumbnail,
      );
    },

    nextPage() {
      if (this.currentPage < this.pageCount) {
        this.currentPage++;
      }
    },

    prevPage() {
      if (this.hasTaxSummary && this.currentPage === 1) {
        this.currentPage = 0; // Go to Tax Summary
      } else if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    updateCurrentPage(event) {
      this.currentPage = event.target.value;
    },
  },

  computed: {
    hasTaxSummary() {
      return (
        this.taxReturn &&
        this.taxReturn.tax_details_enabled &&
        this.taxReturn.tax_details.length > 0
      );
    },
    pageCount() {
      return this.clientVisiblePages.length;
    },
    isLoading() {
      return !(
        this.pdfLoaded &&
        this.pdfLoaded.every(isLoaded => isLoaded) &&
        this.taxReturn &&
        this.taxReturn.tax_return_pages &&
        this.taxReturn.tax_return_pages.length === this.pdfLoaded.length
      );
    },
    ...mapGetters({
      currentUser: 'user',
    }),
    clientVisiblePages() {
      if (this.showFilingInstructionsToClients) {
        return this.taxReturn.tax_return_pages
          .concat(this.taxReturn.filing_instructions)
          .sort((a, b) => a.page - b.page)
          .filter(
            (page, index, self) =>
              index === self.findIndex(t => t.page === page.page),
          );
      } else {
        return this.taxReturn.tax_return_pages;
      }
    },
    filteredPageImages() {
      return this.pageImages.filter(i =>
        this.clientVisiblePages.find(p => p.page === i.page),
      );
    },
    currentPageImage() {
      return this.filteredPageImages[this.currentPage - 1];
    },
    bookmarksFlattened() {
      const flattenBookmarks = bookmarks => {
        let pages = [];
        bookmarks.forEach(bookmark => {
          if (bookmark.pageIndex) {
            // Check if bookmark title indicates multiple pages (e.g., "CA (59 pages)")
            const pageCountMatch = bookmark.title.match(/\((\d+)\s+pages?\)/);
            if (pageCountMatch) {
              const pageCount = parseInt(pageCountMatch[1]);
              // Add all pages covered by this bookmark
              for (let i = 0; i < pageCount; i++) {
                const pageIndex = bookmark.pageIndex + i;
                if (pageIndex <= this.pageImages.length) {
                  pages.push(pageIndex);
                }
              }
            } else {
              pages.push(bookmark.pageIndex);
            }
          }
          if (bookmark.items && bookmark.items.length > 0) {
            pages = pages.concat(flattenBookmarks(bookmark.items));
          }
        });
        return pages;
      };
      return flattenBookmarks(this.bookmarks);
    },
  },

  async created() {
    try {
      this.pdfLoading = true;

      const response = await API.integration.downloadDocument({
        companyId: this.companyId,
        link: this.taxReturn.watermark_enabled
          ? this.taxReturn.watermarked_file
          : this.taxReturn.original_file,
      });

      const pdfSrc =
        response &&
        response.data &&
        response.data.document.data &&
        response.data.document.data.link;

      let useServerImages = false;

      if (this.taxReturn.original_images) {
        try {
          const imagesResponse = await API.taxReturn.getOriginalImages({
            id: this.taxReturn.id,
            companyId: this.companyId,
          });

          this.pageImages = imagesResponse.data.map(image => ({
            page: image.page,
            thumbnail: image.url,
          }));

          useServerImages = true;
        } catch (error) {
          console.error(
            'Failed to load server images, falling back to PDF rendering:',
            error,
          );
        }
      }

      if (useServerImages) {
        this.extractBookmarks(pdfSrc);
      } else {
        this.extractBookmarks(pdfSrc);
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

        this.pdfDoc = await PDFDocument.load(pdfBytes);
        this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });
        this.pageImages = await generatePdfThumbnails(this.pdfData, 1000);
      }

      // page 0 is Tax Summary
      this.currentPage = this.hasTaxSummary ? 0 : 1;
    } catch (error) {
      console.error(error);
    } finally {
      this.pdfLoading = false;
    }
  },
};
</script>

<style lang="css" scoped>
.toolbar {
  position: sticky;
  top: calc(72px + 58px);
  z-index: 1;

  align-items: center;
  box-sizing: border-box;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: center;
  padding: 0 8px;
  width: 100%;

  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-300);
}

.toolbar__pageNavigation {
  display: flex;
  justify-content: center;
  min-width: calc(100% - 360px);
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.pdf-page-container {
  position: relative;
  z-index: 0;

  margin: 0 auto;
  width: 100%;
  flex: auto;
  overflow-y: auto;

  height: 100%;
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
}
.pdf-page-container > div {
  position: relative;

  z-index: 1;
}

.pdf-page-container img {
  max-width: 100%;
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  max-height: calc(100vh - 120px);
  min-width: 260px;
  max-width: 260px;
  overflow: scroll;
  padding: 0px 0px 64px;
  background-color: #fff;
  border-left: 1px solid var(--gray-300);
  box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

.sidebar-card {
  box-shadow: none;
}

@media (max-width: 1260px) {
  .sidebar {
    min-height: calc(100vh - 157px);
    max-height: calc(100vh - 157px);
  }
}

.thumbnail {
  display: flex;
  width: 700px;
  max-width: 100%;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  position: relative;
}

.pdf-review-step__navigation {
  position: sticky !important;
  top: 0;
  z-index: 2 !important;
  gap: 16px;

  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  padding: 4px 8px;
  width: 100%;

  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-200);
}

.pdf-review-step__bottom-navigation {
  gap: 16px;

  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  padding: 4px 8px;
  max-width: 800px;
  margin: 10px auto;
}

.bookmark {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 230px;
}

.filing-instructions-chip {
  /* chip to be smaller */
  >>> .v-chip__content {
    font-size: 10px;
    height: 20px;
    padding: 0 8px;
  }
}
</style>
